import { useEffect, useState } from 'react';
import { Alert, Card, Form } from 'react-bootstrap';
import { FormProvider, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useTranslation } from 'react-i18next';

import { PageHeading } from '../components/common/PageHeading';
import { FormAlert, SelectField, SubmitButton, type SelectOption } from '../components/form';
import { get, post, statusOf } from '../lib/api';
import { usePageTitle } from '../lib/usePageTitle';

const schema = z.object({
    user: z.string().min(1, 'validation.required'),
    role: z.string().min(1, 'validation.required'),
});
type Values = z.infer<typeof schema>;

export function UpdateRolePage() {
    const { t } = useTranslation();
    usePageTitle(t('admin.role.title'));
    const [users, setUsers] = useState<SelectOption[]>([]);
    const [roles, setRoles] = useState<SelectOption[]>([]);
    const [loadError, setLoadError] = useState('');
    const [serverError, setServerError] = useState('');
    const [success, setSuccess] = useState('');

    const methods = useForm<Values>({
        resolver: zodResolver(schema),
        defaultValues: { user: '', role: '' },
        mode: 'onSubmit',
        reValidateMode: 'onChange',
    });

    useEffect(() => {
        get<SelectOption[]>('Update/GetUsers')
            .then((r) => setUsers(r.data ?? []))
            .catch(() => setLoadError(t('admin.role.loadError')));
        get<SelectOption[]>('Update/GetRoles')
            .then((r) => setRoles(r.data ?? []))
            .catch(() => setLoadError(t('admin.role.loadError')));
    }, [t]);

    async function onSubmit(values: Values) {
        setServerError('');
        setSuccess('');
        try {
            await post('update/UpdateRole', { Label: values.user, Value: values.role });
            setSuccess(t('admin.role.success'));
        } catch (err) {
            setServerError(statusOf(err) === 409 ? t('admin.role.roleExists') : t('admin.role.error'));
        }
    }

    return (
        <>
            <PageHeading title={t('admin.role.title')} description={t('admin.role.description')} />
            {loadError && <Alert variant="warning">{loadError}</Alert>}
            <Card body className="shadow-sm" style={{ maxWidth: '32rem' }}>
                <FormProvider {...methods}>
                    <FormAlert onClose={() => setServerError('')}>{serverError || undefined}</FormAlert>
                    {success && (
                        <Alert variant="success" dismissible onClose={() => setSuccess('')}>
                            {success}
                        </Alert>
                    )}
                    <Form noValidate onSubmit={methods.handleSubmit(onSubmit)}>
                        <SelectField name="user" label={t('admin.role.user')} options={users} required />
                        <SelectField name="role" label={t('admin.role.newRole')} options={roles} required />
                        <SubmitButton loading={methods.formState.isSubmitting}>
                            {t('admin.role.submit')}
                        </SubmitButton>
                    </Form>
                </FormProvider>
            </Card>
        </>
    );
}
